import MarkerClusterer from '@googlemaps/markerclustererplus';
import _debounce from 'lodash.debounce';
import _groupBy from 'lodash.groupby';
import Vue from 'vue';
import { COUNTRIES } from '../consts/countries';
import { MAP_STYLE } from '../consts/map-style';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    geocodeWithFrenchFallback,
    resolveFrenchGeocodeParams,
} from '../utils';

// Matches the id in templates/_includes/_brand-stores-map-markers.twig.
const MARKERS_ELEMENT_ID = 'brand-stores-map-markers';

Vue.component('brand-stores-map-component', {
    props: {
        mapCenterLat: Number,
        mapCenterLong: Number,
        mapZoomFactor: Number,
        siteHandle: String,
        europeMap: Boolean,
        storeSearchError: String,
        storeSearchCityError: String,
        siteUrl: String,
        showOnlyServiceStores: Boolean,
        isSearchOutsideModal: Boolean,
        cdnUrl: {
            type: String,
            default: '',
        },
        hasDealerCenter: {
            type: Boolean,
            default: false,
        },
        stores: {
            type: Array,
            default: () => [],
        },
        // Marker-only inline set, read from the `_brand-stores-map-markers` JSON
        // block: pin clicks lazy-load detail (separate from the europeMap
        // zoom/center flag).
        markersOnly: {
            type: Boolean,
            default: false,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            storeInfoShowed: false,
            selectedStore: [],
            storeDetailCache: {},
            pendingStoreId: null,
            map: null,
            city: '',
            geocoder: null,
            storeFilter: 'dealer',
            autocomplete: null,
            markerCluster: null,
            markers: null,
            searchError: null,
            zoom: null,
            errors: null,

            // Handle show button
            isServiceStore: false,
            isTestdriveButtonHidden: false,

            store: null,
            zipCoords: null,
            searchQueryIsDirty: false,
            nearbyProximityStores: null,
            storesWithDistance: null,
            // Error vars for zip api request
            needMoreInput: false,
            apiRequestLoading: false,
            belgiumCountryHandles: ['mgMotorsBelgiumFr', 'mgMotorsBelgiumNl'],
            countryValue: COUNTRIES,
            filteredStores: null,
            storesData: [],
        };
    },

    computed: {
        hasImStores() {
            return this.filteredStores?.imStore?.length > 0;
        },
    },

    watch: {
        storeFilter() {
            this.storeInfoShowed = false;
        },

        store() {
            if (this.store && this.store.length < 4) {
                this.needMoreInput = true;
                this.nearbyProximityStores = null;
                return;
            }

            if (this.selectedBrandStore) {
                if (this.selectedBrandStore.title === this.store) {
                    return;
                }
            }

            this.selectedBrandStore = null;
            this.needMoreInput = false;
            this.searchByZipGoogleApi();
        },

        selectedStore(value) {
            if (!value || !value.specialStoreType) {
                return;
            }

            this.isServiceStore =
                value.specialStoreType.value === 'serviceStore';
            this.isTestdriveButtonHidden = value.title.includes('Westfield');
            EventBus.$emit('brandstore-selected', value);
        },

        city: function (newVal, oldVal) {
            if (newVal === undefined) {
                this.city = oldVal;
                return;
            }
            this.handleLocationLabelMoved(newVal);
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        Vue.nextTick(() => {
            this.city = null;
            this.store = null;
        });
        this.init();
        this.handleClickOutside();
        this.handleCategoriesScrollOnDrag();
        setTimeout(() => {
            this.appendPacContainerToOutsideSearchInput();
        }, 1000);
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        handleLocationLabelMoved(textContent) {
            const label = this.$el.querySelector('#location-label');
            if (!label) return;
            textContent && textContent?.length > 0
                ? label.classList.add('new-input__label--moved')
                : label.classList.remove('new-input__label--moved');
        },

        appendPacContainerToOutsideSearchInput() {
            if (!this.isSearchOutsideModal) return;
            const pacContainer = document.body.querySelector('.pac-container');
            if (!pacContainer) return;

            const outsideSearchInput = document.body.querySelector(
                '.location-new-modal__search-outside-modal',
            );
            if (!outsideSearchInput) return;

            outsideSearchInput.append(pacContainer);
        },

        handleCategoriesScrollOnDrag() {
            if (!this.siteHandle.includes('mgMotorsSwitzerland')) return;

            const slider = document.querySelector(
                '.location-new-modal__categories',
            );
            let isDown = false;
            let startX;
            let scrollLeft;

            slider.addEventListener('mousedown', (e) => {
                isDown = true;
                slider.classList.add('active');
                startX = e.pageX - slider.offsetLeft;
                scrollLeft = slider.scrollLeft;
            });

            slider.addEventListener('mouseleave', () => {
                isDown = false;
                slider.classList.remove('active');
            });

            slider.addEventListener('mouseup', () => {
                isDown = false;
                slider.classList.remove('active');
            });

            slider.addEventListener('mousemove', (e) => {
                if (!isDown) return;
                e.preventDefault();
                const x = e.pageX - slider.offsetLeft;
                const walk = (x - startX) * 1; // Adjust scroll speed multiplier if needed
                slider.scrollLeft = scrollLeft - walk;
            });
        },

        handleClickOutside() {
            const modal = document.querySelector('.location-new-modal');
            if (!modal) return;

            window.addEventListener('click', (e) => {
                const isImageSizeInTarget = 'imageSize' in e.target;
                if (!isImageSizeInTarget && !modal.contains(e.target)) {
                    this.storeInfoShowed = false;
                    return;
                }
            });
        },

        handleActiveTitle(filter) {
            return this.storeFilter == filter
                ? 'location-new-modal__category--active'
                : '';
        },

        closeStoreInfo() {
            this.storeInfoShowed = false;
        },

        init() {
            EventBus.$once('google-maps-ready', this.initMap);

            this.storesData = this.markersOnly
                ? this.readInlineMarkers()
                : this.stores;
            this.prepareInlineStores();
            this.intersect();
        },

        // Compact `[id, title, lat, lng, type]` tuples reshaped into the store
        // shape the rest of the component reads. Frozen so Vue doesn't make
        // ~2.7k marker objects reactive.
        readInlineMarkers() {
            const source = document.getElementById(MARKERS_ELEMENT_ID);
            if (!source) return [];

            try {
                return Object.freeze(
                    JSON.parse(source.textContent).map(
                        ([id, title, lat, lng, specialStoreType]) => ({
                            id,
                            title,
                            lat,
                            lng,
                            specialStoreType: { value: specialStoreType },
                        }),
                    ),
                );
            } catch {
                return [];
            }
        },

        prepareInlineStores() {
            this.storesData.forEach((store) => this.rewriteButtonUrls(store));

            this.filteredStores = _groupBy(
                this.storesData,
                'specialStoreType.value',
            );
        },

        searchByZipGoogleApi: _debounce(function () {
            this.makeApiRequest();
        }, 300),

        makeApiRequest() {
            this.apiRequestLoading = true;
            const isRestrictedCountry = [
                'mgMotorsFrance',
                'mgMotorsNorway',
            ].includes(this.siteHandle);
            const componentRestrictions = !isRestrictedCountry
                ? {
                      componentRestrictions: {
                          country: this.countryValue[this.siteHandle],
                      },
                  }
                : undefined;

            this.geocoder.geocode(
                {
                    ...componentRestrictions,
                    address: encodeURI(this.store),
                },
                (results, status) => {
                    if (status !== 'OK') {
                        console.error(
                            'Geocode was not successful for the following reason: ' +
                                status,
                        );
                        this.searchError = this.storeSearchError; //'Couldn\'t retrieve your location, please try again';
                        return;
                    }

                    if (!results[0]) return;

                    this.searchError = '';
                    this.apiRequestLoading = false;
                    this.zipCoords = {
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng(),
                    };

                    if (!this.zipCoords) {
                        return;
                    }
                    const storesData = this.mapStoresByFilter(this.storeFilter);
                    this.getNearbyBrandstores(this.zipCoords, storesData);
                },
            );
        },

        mapStoresByFilter(storeFilter) {
            if (storeFilter === 'service') {
                return this.filteredStores.serviceStore;
            }

            if (storeFilter === 'imStore') {
                return this.filteredStores.imStore;
            }

            return [
                ...(this.filteredStores.none || []),
                ...(this.filteredStores.testDriveStore || []),
            ];
        },

        getNearbyBrandstores(userLocation, brandstores) {
            this.storesWithDistance = Object.values(brandstores).map(
                (store) => {
                    const d = this.getDistance(userLocation, store);

                    return {
                        ...store,
                        distance: d,
                    };
                },
            );

            this.nearbyProximityStores = this.storesWithDistance
                .sort((a, b) => {
                    if (a.distance < b.distance) {
                        return -1;
                    }

                    if (a.distance > b.distance) {
                        return 1;
                    }

                    return 0;
                })
                .slice(0, 1);

            if (this.nearbyProximityStores != null) {
                const pos = {
                    lat: parseFloat(this.nearbyProximityStores[0].lat),
                    lng: parseFloat(this.nearbyProximityStores[0].lng),
                };

                this.map.setCenter(pos);
                this.map.setZoom(12);

                this.storeInfoShowed = false;
                this.selectedStore = this.nearbyProximityStores[0];

                this.showStoreInfo(this.nearbyProximityStores[0].id);
            }
        },

        getDistance(userLocation, brandstore) {
            // Bron: https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
            const radius = 6371.071; // Radius of the Earth in KM

            const radiusLat1 = userLocation.lat * (Math.PI / 180); // Convert degrees to radians
            const radiusLat2 = brandstore.lat * (Math.PI / 180); // Convert degrees to radians
            const differenceLat = radiusLat2 - radiusLat1; // Radian difference (latitudes)
            const differenceLng =
                (brandstore.lng - userLocation.lng) * (Math.PI / 180); // Radian difference (longitudes)

            const distance =
                2 *
                radius *
                Math.asin(
                    Math.sqrt(
                        Math.sin(differenceLat / 2) *
                            Math.sin(differenceLat / 2) +
                            Math.cos(radiusLat1) *
                                Math.cos(radiusLat2) *
                                Math.sin(differenceLng / 2) *
                                Math.sin(differenceLng / 2),
                    ),
                );

            return distance;
        },

        /**
         * Initialize the Google Map
         *
         * @return {Void}
         */
        initMap() {
            // If map must show the europe map, set small zoom
            if (!this.hasDealerCenter) {
                this.zoom = this.europeMap ? 4 : this.mapZoomFactor;
            } else {
                this.zoom = 16;
            }

            // If map must show the europe map, set lat and long so europe is visible
            const lat = this.europeMap ? 52.370216 : this.mapCenterLat;
            const long = this.europeMap ? 4.895168 : this.mapCenterLong;

            this.map = new google.maps.Map(document.getElementById('gmap'), {
                zoom: this.zoom,
                center: { lat: lat, lng: long },
                styles: MAP_STYLE,
                scrollwheel: false,
                disableDefaultUI: true,
            });

            this.buildMarkers();

            // Adding Google maps Geocoder
            this.geocoder = new google.maps.Geocoder();

            // Adding Google maps Autocomplete (places API)
            this.addAutocomplete();

            const infoWindow = new google.maps.InfoWindow();
            infoWindow.setOptions({
                pixelOffset: new google.maps.Size(0, -30),
            });

            // Only use automatic geolocation when we don't have a specific dealer center
            if (!this.hasDealerCenter) {
                this.addGeolocation();
            }
        },

        shouldRenderStore(store) {
            // GENN-2975: CH removed the Service tab (GENN-1703), so Service Store
            // entries must neither render nor count towards the cluster badge.
            // Excluding them from the markers array (instead of only hiding them)
            // stops a Regular + Service Store at the same address showing a stuck "2".
            if (!this.siteHandle.includes('Switzerland')) return true;

            return store.specialStoreType.value !== 'serviceStore';
        },

        hasValidCoordinates(store) {
            return (
                Number.isFinite(parseFloat(store.lat)) &&
                Number.isFinite(parseFloat(store.lng))
            );
        },

        markerIconUrl(type) {
            if (type === 'imStore') {
                return `${this.cdnUrl}/images/im-brandstore-pin.png`;
            }

            if (
                type === 'salesRepresentative' &&
                this.siteHandle === 'mgMotorsFrance'
            ) {
                return `${this.cdnUrl}/images/marker-sales-representative.png`;
            }

            return `${this.cdnUrl}/images/brandstore-pin.png`;
        },

        buildMarkers() {
            this.markers = this.storesData
                .filter((store) => this.hasValidCoordinates(store))
                .filter((store) => this.shouldRenderStore(store))
                .map((store) => {
                    const type = store.specialStoreType.value;

                    const marker = new google.maps.Marker({
                        position: {
                            lat: parseFloat(store.lat),
                            lng: parseFloat(store.lng),
                        },
                        icon: this.markerIconUrl(type),
                        id: store.id,
                        type: type,
                    });

                    google.maps.event.addListener(marker, 'click', () => {
                        this.showStoreInfo(marker.id);
                    });

                    return marker;
                });

            this.markerCluster = new MarkerClusterer(this.map, this.markers, {
                imagePath: `${this.cdnUrl}/images/black-pin`,
                imageExtension: 'png',
            });

            this.setFilter(this.showOnlyServiceStores ? 'service' : 'dealer');
        },

        getAutocompleteOptions() {
            if (this.siteHandle == 'default') {
                return {
                    types: ['(cities)'],
                    fields: ['formatted_address'],
                };
            }

            return {
                types: ['(cities)'],
                fields: ['formatted_address'],
                //because we use 'Autocomplete' we can use an array (FR), note max length = 5
                componentRestrictions: {
                    country: this.countryValue[this.siteHandle],
                },
            };
        },

        addAutocomplete() {
            const input = document.getElementById('autocomplete');
            const options = this.getAutocompleteOptions();
            this.autocomplete = new google.maps.places.Autocomplete(
                input,
                options,
            );

            this.autocomplete.addListener('place_changed', this.onPlaceChanged);
        },

        addGeolocation() {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const pos = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };

                        this.map.setCenter(pos);
                        this.map.setZoom(8);
                    },
                    () => {
                        console.log(
                            "ERROR: Something went wrong, couldn't retrieve current position",
                        );
                    },
                );
            } else {
                // Browser doesn't support Geolocation
                console.log("Browser doesn't support Geolocation");
            }
        },

        onPlaceChanged() {
            const place = this.autocomplete.getPlace();

            if (!place.formatted_address) return;

            if (this.store == null) {
                this.city = place.formatted_address;

                this.searchError = null;
                this.codeAddress();
            } else {
                this.needMoreInput = false;
                this.store = place.formatted_address;
                this.searchError = null;
            }
        },

        intersect() {
            const options = {
                rootMargin: '300px',
                threshold: 0,
            };

            const map = document.getElementById('map');

            const observer = new IntersectionObserver((entries, observer) => {
                // Detect intersection https://calendar.perfplanet.com/2017/progressive-image-loading-using-intersection-observer-and-sqip/#comment-102838
                const isIntersecting =
                    typeof entries[0].isIntersecting === 'boolean'
                        ? entries[0].isIntersecting
                        : entries[0].intersectionRatio > 0;
                if (isIntersecting) {
                    this.injectGoogleMaps();
                    observer.unobserve(map);
                }
            }, options);

            observer.observe(map);
        },

        /**
         * Injects the Google Maps scripts when it is not there yet.
         *
         * @return {Void}
         */
        injectGoogleMaps() {
            const tag = document.createElement('script');
            tag.setAttribute('type', 'text/javascript');
            tag.defer = true;
            tag.async = true;
            tag.setAttribute(
                'src',
                '//maps.google.com/maps/api/js?key=AIzaSyBXLteX7ErcVvjvFLHB1_TMCzq55wVb5Bg&libraries=places&callback=mapsCallback',
            );
            (
                document.getElementsByTagName('head')[0] ||
                document.documentElement
            ).appendChild(tag);
        },

        // Lazy-loads one store's full detail (Europe map), memoized per id.
        fetchStoreDetail(id) {
            if (this.storeDetailCache[id]) {
                return Promise.resolve(this.storeDetailCache[id]);
            }

            return axios
                .get('/actions/mg-business-logic/brand-stores/store', {
                    params: { id },
                })
                .then((response) => {
                    const store = response.data?.data;
                    if (!store) return null;

                    this.rewriteButtonUrls(store);
                    this.storeDetailCache[id] = store;

                    return store;
                })
                .catch(() => null);
        },

        /**
         * The store payload's custom button URLs are built as absolute links to
         * the main site host. On a dealer subdomain (e.g. unikomerc.mgmotor.hr)
         * `siteUrl` still resolves to the main host, so those links would send
         * the user off the dealer subdomain. Rewrite any button URL that targets
         * the main site origin to the current origin so links stay on the dealer
         * subdomain. On the main site this is a no-op, and external URLs are left
         * untouched.
         *
         * @param {Object} store
         *
         * @return {Void}
         */
        rewriteButtonUrls(store) {
            if (!store?.customButtons?.length || !this.siteUrl) return;

            let siteOrigin;
            try {
                siteOrigin = new URL(this.siteUrl).origin;
            } catch (error) {
                return;
            }

            const currentOrigin = window.location.origin;
            if (siteOrigin === currentOrigin) return;

            store.customButtons.forEach((button) => {
                if (
                    typeof button.buttonUrl === 'string' &&
                    button.buttonUrl.startsWith(siteOrigin)
                ) {
                    button.buttonUrl =
                        currentOrigin +
                        button.buttonUrl.slice(siteOrigin.length);
                }
            });
        },

        /**
         * Show store info when map marker on map is clicked
         *
         * @return {Void}
         */
        showStoreInfo(clickedId) {
            if (this.storeInfoShowed && clickedId == this.selectedStore.id) {
                this.storeInfoShowed = false;
                this.selectedStore = [];

                return;
            }

            const marker = this.storesData.find(
                (store) => store.id == clickedId,
            );

            // A marker whose id has no matching store (e.g. a feed/site mismatch)
            // must not open the panel — rendering it would read `.title` on undefined.
            if (!marker) return;

            if (!this.markersOnly) {
                this.openStoreInfo(marker);

                return;
            }

            // Ignore a repeat click on the pin already loading.
            if (this.pendingStoreId == clickedId) return;

            this.pendingStoreId = clickedId;
            this.fetchStoreDetail(clickedId).then((store) => {
                // Drop a stale response if another pin was clicked meanwhile.
                if (this.pendingStoreId != clickedId) return;

                this.pendingStoreId = null;
                this.openStoreInfo(store ?? marker);
            });
        },

        openStoreInfo(store) {
            this.selectedStore = store;
            this.storeInfoShowed = true;
            this.trackStoreConversion();
        },

        trackStoreConversion() {
            if (!checkCCM19Consent() && !checkOneTrustConsent()) return;

            if (this.$root.handle == 'mgMotorsFrance') {
                gtag('event', 'conversion', {
                    allow_custom_scripts: true,
                    send_to: 'DC-10256326/mg-autos/fr_in003+unique',
                }); //french
            }

            if (this.$root.handle == 'mgMotorsNorway') {
                gtag('event', 'conversion', {
                    allow_custom_scripts: true,
                    send_to: 'DC-10210913/retar0/no_re000+standard',
                }); //norway
            }
        },

        /**
         * Raised when zoom in button of the map is clicked.
         *
         * @param   {Event}
         * @returns {Void}
         */
        onMapZoomInClick(event) {
            this.map.setZoom(this.map.getZoom() + 1);
            this.zoom = this.map.getZoom();
        },

        /**
         * Raised when the zoom out button of the map is clicked.
         *
         * @param {Event} event
         * @returns {Void}
         */
        onMapZoomOutClick(event) {
            this.map.setZoom(this.map.getZoom() - 1);
            this.zoom = this.map.getZoom();
        },

        codeAddress() {
            let tempCity = this.city;

            let country = this.countryValue[this.siteHandle];

            if (this.siteHandle === 'mgMotorsFrance') {
                ({ address: tempCity, country } = resolveFrenchGeocodeParams(
                    this.city,
                ));
            }

            if (this.siteHandle.includes('Switzerland')) {
                country = this.countryValue[this.siteHandle];
            }

            if (this.siteHandle === 'mgMotorsNorway') {
                country = 'NO';

                // Svalbard and Jan Mayen
                const locations = [
                    { code: '8099', name: 'Jan Mayen' },
                    { code: '9170', name: 'Longyearbyen' },
                    { code: '9171', name: 'Longyearbyen' },
                    { code: '9172', name: 'Isfjord' },
                    { code: '9173', name: 'Ny-Ålesund' },
                    { code: '9174', name: 'Hopen' },
                    { code: '9175', name: 'Sveagruva' },
                    { code: '9176', name: 'Bjørnøya' },
                    { code: '9177', name: 'Hornsund' },
                    { code: '9178', name: 'Barentsburg' },
                ];

                locations.forEach((location) => {
                    if (
                        location.code.includes(tempCity) ||
                        location.name.includes(tempCity)
                    ) {
                        country = 'SJ';
                        tempCity = `${location.code} ${location.name}`;
                    }
                });
            }

            const handleGeocodeResult = (results, status) => {
                if (status == 'OK' && !this.nearbyProximityStores) {
                    this.map.setCenter(results[0].geometry.location);

                    let zoomValue = 10;
                    if (this.siteHandle == 'mgMotorsSpain') zoomValue = 13;
                    if (this.europeMap) zoomValue = 8;

                    this.map.setZoom(zoomValue);
                    this.searchError = null;
                } else {
                    this.searchError = this.storeSearchCityError
                        ? this.storeSearchCityError.replace('{city}', this.city)
                        : 'Couldn\'t locate "' +
                          this.city +
                          '", please try again';
                }
            };

            geocodeWithFrenchFallback(
                this.geocoder,
                { address: tempCity, country },
                this.siteHandle,
                handleGeocodeResult,
            );
        },

        setFilter(value) {
            this.storeFilter = value;

            // I've separated the logic into two functions to make it more readable
            // Since belgium has different filtering logic of showing markers, we need to handle it separately
            this.handleShowMarkerForOtherCountries();
            this.handleShowMarkerForBelgium();

            this.markerCluster.setIgnoreHidden(true);
            this.markerCluster.repaint();
        },

        handleShowMarkerForBelgium() {
            if (!this.belgiumCountryHandles.includes(this.siteHandle)) return;

            const shouldShowMarker = (storeFilter, type) => {
                if (storeFilter === 'service') {
                    return type === 'serviceStore';
                }
                if (storeFilter === 'imStore') return type === 'imStore';
                return true;
            };

            this.markerCluster.markers_.forEach((marker) => {
                marker.setVisible(
                    shouldShowMarker(this.storeFilter, marker.type),
                );
            });
        },

        handleShowMarkerForOtherCountries() {
            if (this.belgiumCountryHandles.includes(this.siteHandle)) return;

            const shouldShowMarker = (filter, type) => {
                if (filter === 'imStore') return type === 'imStore';
                if (filter === 'service') return type === 'serviceStore';
                return !['serviceStore', 'imStore'].includes(type);
            };

            this.markerCluster.markers_.forEach((marker) => {
                marker.setVisible(
                    shouldShowMarker(this.storeFilter, marker.type),
                );
            });
        },

        isNullOrEmpty(str) {
            return !str || !str.trim();
        },

        handlePermission() {
            navigator.permissions
                .query({ name: 'geolocation' })
                .then((result) => {
                    if (result.state == 'granted') {
                        this.report(result.state);
                        // geoBtn.style.display = 'none';
                        this.addGeolocation();
                    } else if (result.state == 'prompt') {
                        this.report(result.state);
                        // geoBtn.style.display = 'none';
                        navigator.geolocation.getCurrentPosition(
                            revealPosition,
                            positionDenied,
                            geoSettings,
                        );
                        this.addGeolocation();
                    } else if (result.state == 'denied') {
                        this.report(result.state);
                        // geoBtn.style.display = 'inline';
                        this.searchError =
                            'Access is denied to your current position, please change browser settings to use this function';
                    }
                    result.onchange = () => {
                        this.report(result.state);
                    };
                });
        },

        report(state) {
            console.log('Permission ' + state);
        },

        datalayerMailto() {
            if (this.siteHandle != 'mgMotorsFrance') return;

            dataLayer.push({
                event: 'potentialLead',
                eventCategory: 'Potential Leads',
                eventAction: 'Dealer mailto',
                eventLabel: this.selectedStore.title,
            });
        },

        datalayerPhone() {
            if (this.siteHandle != 'mgMotorsFrance') return;
            dataLayer.push({
                event: 'potentialLead',
                eventCategory: 'Potential Leads',
                eventAction: 'Dealer telephone',
                eventLabel: this.selectedStore.title,
            });
        },

        datalayerRoute() {
            if (this.siteHandle != 'mgMotorsFrance') return;
            dataLayer.push({
                event: 'potentialLead',
                eventCategory: 'Potential Leads',
                eventAction: 'Dealer route',
                eventLabel: this.selectedStore.title,
            });
        },
    },
});
